import React, { useState, useCallback } from 'react'

import backgroundImage from '../images/header-17.jpg'
import { Link } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import useTitle from '../hooks/useTitle'

const ERROR_CODE_WHITELIST = [
  "LimitExceededException",
  "UserNotFoundException",
]

type ForgotProgressStep = "INITIAL" | "ENTER_CODE"

interface InitialFormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement
}

interface PasswordRefreshFormElements extends HTMLFormControlsCollection {
  code: HTMLInputElement
  newPassword: HTMLInputElement
  newPasswordConfirmation: HTMLInputElement
}

const ForgotPassword: React.FC = () => {
  useTitle("Reset password | Speech2")

  const [ forgotPasswordStep, setForgotPasswordStep ] = useState<ForgotProgressStep>('INITIAL')
  const [ errorMessage, setErrorMessage ] = useState<string>()
  const [ sentEmailAddress, setSentEmailAddress ] = useState<string>()

  const login = useCallback(
    async (email: string, password: string) => {
      await Auth.signIn(email, password)
    },
    [  ]
  )

  const sendCode = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setErrorMessage(undefined)

      const elements = event.currentTarget.elements as InitialFormElements
      const email = elements.email.value

      try {
        setSentEmailAddress(email)
        await Auth.forgotPassword(email)
        setForgotPasswordStep('ENTER_CODE')

      } catch (err) {
        if (ERROR_CODE_WHITELIST.indexOf(err.code) >= 0) {
          setErrorMessage(err.message)
        } else {
          throw err
        }
      }
    },
    [  ]
  )

  const setNewPassword = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const elements = event.currentTarget.elements as PasswordRefreshFormElements
      const code = elements.code.value
      const newPassword = elements.newPassword.value
      const newPasswordConfirmation = elements.newPasswordConfirmation.value

      if (!sentEmailAddress) {
        setErrorMessage("No e-mail address.")
        return
      }

      if (newPassword !== newPasswordConfirmation) {
        setErrorMessage("The password and confirmation didn't match. Please try again.")
        elements.newPassword.value = ""
        elements.newPasswordConfirmation.value = ""
        return
      }

      setErrorMessage(undefined)

      try {
        await Auth.forgotPasswordSubmit(
          sentEmailAddress,
          code,
          newPassword
        )
        await login(sentEmailAddress, newPassword)
      } catch (err) {
        if (err.code === "InvalidPasswordException") {
          setErrorMessage(err.message)
        } else {
          console.log("Cognito error in sessionCompleteNewPasswordChallenge", err)
          throw err
        }
      }
    },
    [ login, sentEmailAddress ]
  )

  const initialScreen = forgotPasswordStep === 'INITIAL' && (
    <div className="col-11 col-md-8 col-lg-10 col-xl-9 py-4">
      <h1 className="h2 text-center">Forgot your password?</h1>
      {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
      <form onSubmit={sendCode}>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            tabIndex={1}
            id="email"
            name="email"
            type="email"
            className="form-control"
            placeholder="Enter your email address"
          />
        </div>

        <div className="form-group">
          <button
            tabIndex={3}
            className="btn btn-block btn-lg btn-primary"
            type="submit"
          >
            Send recovery code
          </button>
        </div>
      </form>
      <div className="text-center text-small mt-3">
        <span>Don't have an account? <Link to="/signup">Sign up here</Link></span>
      </div>
    </div>
  )

  const passwordRefreshScreen = forgotPasswordStep === 'ENTER_CODE' && (
    <div className="col-11 col-md-8 col-lg-10 col-xl-9 py-4">
      <h1 className="h2 text-center">Forgot your password?</h1>
      <div className="alert alert-primary">
        We've sent a verification code to {sentEmailAddress}. Make sure to check your spam
        if it hasn't arrived in a few minutes.
      </div>
      {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
      <form onSubmit={setNewPassword}>
        <div className="form-group">
          <div className="d-flex justify-content-between">
            <label htmlFor="code">Verification code:</label>
          </div>
          <input
            id="code"
            name="code"
            type="text"
            className="form-control"
            placeholder="000000"
          />
        </div>
        <div className="form-group">
          <div className="d-flex justify-content-between">
            <label htmlFor="newPassword">Password:</label>
          </div>
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            className="form-control"
            placeholder="Choose a password"
          />
        </div>
        <div className="form-group">
          <div className="d-flex justify-content-between">
            <label htmlFor="newPasswordConfirmation">Password (once more, please):</label>
          </div>
          <input
            id="newPasswordConfirmation"
            name="newPasswordConfirmation"
            type="password"
            className="form-control"
            placeholder="Choose a password"
          />
        </div>
        <div className="form-group">
          <button
            className="btn btn-block btn-lg btn-primary"
            type="submit"
          >
            Reset Password
          </button>
        </div>
      </form>
    </div>
  )

  return (
    <section className="row no-gutters p-0 height-100 align-items-initial">
      <div className="col-lg d-lg-flex order-lg-2 height-30">
        <img src={backgroundImage} alt="background" className="bg-image" />
      </div>
      <div className="col-lg-5 p-0 d-flex bg-white align-items-lg-center">
        <div className="row no-gutters flex-fill justify-content-center">
          { initialScreen }
          { passwordRefreshScreen }
        </div>
      </div>
    </section>
  )
}

export default ForgotPassword
